import { Runnit, RUNNIT_TYPE } from '@run-diffusion/shared';
import { Query } from '@/firebase';

// What the Boards carousel shows. Was NUM_FEATURED_TEMPLATES_TO_SHOW, local to
// RunnitBoardsManagement.
export const FEATURED_TEMPLATES_CAROUSEL_LIMIT: number = 50;

// The Templates page grids the whole featured set rather than a strip of it.
export const FEATURED_TEMPLATES_GRID_LIMIT: number = 2000;

// Admin > Featured Templates. Well above any plausible curated set, and a hard
// stop so a runaway read cannot happen.
export const ADMIN_FEATURED_TEMPLATES_LIMIT: number = 500;

/**
 * The one definition of "the featured templates a user sees", in the order they
 * see them. Every user-facing surface and the admin page's user preview call
 * this, so the preview cannot silently drift from production.
 *
 * Ordering by featuredOrder rather than createdAt is what makes the carousel a
 * curated strip instead of a newest-first feed. It also means the composite
 * index is SPARSE_ALL on featuredOrder: a featured template without the field
 * is not in the index and never renders. That invariant is enforced at both
 * write sites (Admin > Featured Templates, and the board menu's Feature action)
 * and was established for existing documents by the 2026-08-12 backfill.
 *
 * index created: runnits - deletedAt Ascending isFeatured Ascending type Ascending featuredOrder Ascending __name__ Ascending
 *
 * That index is ascending only. An `orderBy('featuredOrder', 'desc')` on the
 * same filters is NOT served by it and fails with FAILED_PRECONDITION, so read
 * the whole set rather than reaching for a descending limit(1).
 */
export function buildFeaturedTemplatesQuery (runnitsRef: Query, limit: number): Query {
	return runnitsRef
		.where('type', '==', RUNNIT_TYPE.TEMPLATE)
		.where('isFeatured', '==', true)
		.where('deletedAt', '==', null)
		.orderBy('featuredOrder', 'asc')
		.limit(limit);
}

/**
 * The admin view of the same set. Two deliberate differences from the user
 * query: soft-deleted templates stay in, because an admin has to see that a
 * featured template is no longer reaching users; and there is no orderBy, so a
 * document with no featuredOrder is still returned rather than being dropped by
 * the sparse index. Sorting happens in memory via sortByFeaturedOrder.
 *
 * Equality filters only and no orderBy, which Firestore serves by merging
 * single-field indexes. No composite index entry is needed.
 */
export function buildAdminFeaturedTemplatesQuery (runnitsRef: Query, limit: number): Query {
	return runnitsRef
		.where('type', '==', RUNNIT_TYPE.TEMPLATE)
		.where('isFeatured', '==', true)
		.limit(limit);
}

function orderOf (runnit: Runnit): number {
	return typeof runnit.featuredOrder === 'number' ? runnit.featuredOrder : Number.MAX_SAFE_INTEGER;
}

/** Ascending by featuredOrder, with unordered templates last rather than at 0. */
export function sortByFeaturedOrder (runnits: Runnit[]): Runnit[] {
	return runnits.slice().sort((a: Runnit, b: Runnit) => orderOf(a) - orderOf(b));
}

/** Where a newly featured template goes: the end of the curated list. */
export function nextFeaturedOrder (featured: Runnit[]): number {
	const orders: number[] = featured
		.map((runnit: Runnit) => runnit.featuredOrder)
		.filter((order: any) => typeof order === 'number') as number[];
	if (!orders.length) return 0;
	return Math.max(...orders) + 1;
}

/**
 * Dense renumber from zero, so the order never depends on the gaps left by
 * de-featuring. Same approach as Manage Kits' sortOrder.
 */
export function renumberFeaturedOrder (ordered: Runnit[]): { id: string; featuredOrder: number }[] {
	return ordered.map((runnit: Runnit, index: number) => ({ id: runnit.id, featuredOrder: index }));
}

/** Why users cannot see this featured template, or '' when they can. */
export function featuredVisibilityIssue (runnit: Runnit): string {
	if (runnit.deletedAt) return 'This template is deleted, so users cannot see it.';
	if (typeof runnit.featuredOrder !== 'number') return 'This template has no order yet, so it is missing from the carousel.';
	return '';
}
